// frontend/src/tabs/tabsContexts.js
//
// The contexts the tabs subsystem shares, and the accessors that read them:
//   TabsStateContext / TabsDispatchContext — filled by <TabsProvider>
//   TabIdContext      — set by TabsHost for each rendered tab so per-tab hooks
//                       key their storage by the tab the page belongs to, not
//                       the active tab
//   TabActiveContext  — whether the tab is the one on screen (useTabActive)
//   TabParamsContext  — the tab's own route params (useTabParams)
//
// Public surface is in useTabs.js. Pages consume useTabPersistedState and
// useDirtyTabs — neither needs to know about this file.

import { createContext, useContext } from 'react';

export const MAX_TABS = 10;

// Thrown by useTabs().open when the cap is reached; useTabs().openTab turns it
// into the cap-reached event AppToast shows (capEvent.js).
export class TabCapError extends Error {
  constructor() {
    super('Tab cap reached');
    this.name = 'TabCapError';
    this.code = 'tabs.capReached';
  }
}

export const TabsStateContext = createContext(null);
export const TabsDispatchContext = createContext(null);

// Set by TabsHost around each rendered tab so per-tab hooks
// (useTabPersistedState, useDirtyTabs) know which tab they belong to even
// when their host is in a hidden tab.
export const TabIdContext = createContext(null);

// Whether the tab a component belongs to is the one on screen. A tab stays
// mounted and live once it has been shown (tabs/TabPane.jsx), so work that
// only serves what the merchant is looking at — polling, a reload after an
// import — asks this and waits for the tab to come back. True outside the
// tabbed shell.
export const TabActiveContext = createContext(true);

export function useTabActive() {
  return useContext(TabActiveContext);
}

// A query's `refetchInterval` for a page inside a tab: `ms` while the tab is
// on screen, `false` (no polling) while it is hidden.
export function useTabPollInterval(ms) {
  return useTabActive() ? ms : false;
}

// The tab this component belongs to, or null outside the tabbed shell.
//
// Optional is the contract, and the caller decides what absence means:
// useTabPersistedState throws on it, useGridState drops its per-tab layer and
// keeps its per-user one. Neither substitutes a stand-in tab id. What happens
// to a grid outside the shell is asserted, not asserted-to-be-fine — see
// "outside the tabbed shell" in hooks/useGridState.tabRestore.test.jsx.
export function useOptionalTabId() {
  return useContext(TabIdContext);
}

// Per-tab route params, derived by matchPath() against the tab's path
// (NOT the global URL). Pages should consume via useTabParams() instead
// of react-router's useParams() — useParams reads the browser URL, which
// is the active tab's path, not a hidden tab's.
export const TabParamsContext = createContext(null);

export function useTabsStateContext() {
  const ctx = useContext(TabsStateContext);
  if (!ctx) {
    throw new Error('useTabs must be used inside <TabsProvider>');
  }
  return ctx;
}

export function useTabsDispatch() {
  const dispatch = useContext(TabsDispatchContext);
  if (!dispatch) {
    throw new Error('tabs dispatch used outside <TabsProvider>');
  }
  return dispatch;
}
